import React, { useState, useEffect } from 'react';
import { X, Calendar, CreditCard, Download, ArrowRight, AlertTriangle, Building2, User, Mail, Phone, MapPin, Briefcase, FileText } from 'lucide-react';
import { jsPDF } from 'jspdf';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/currency';
import EmailFormHandler from './EmailFormHandler';

interface PayMoloFormProps {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  productPrice: number;
}

const MIN_DOWNPAYMENT = 150000; // 150,000 FCFA minimum
const PAYMENT_PERIODS = [3, 6, 12, 18, 24];

interface PaymentPlan {
  downPayment: number;
  remainingAmount: number;
  monthlyPayment: number;
  duration: number;
  totalAmount: number;
}

const PayMoloForm: React.FC<PayMoloFormProps> = ({
  isOpen,
  onClose,
  productName,
  productPrice
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [accountType, setAccountType] = useState<'individual' | 'business'>('individual');
  const [paymentMode, setPaymentMode] = useState<'cash' | 'credit'>('cash');
  const [formData, setFormData] = useState({
    downPayment: MIN_DOWNPAYMENT,
    duration: 12,
    promoCode: '',
    // Personal info
    fullName: '',
    neighborhood: '',
    licenseNumber: '',
    phone: '',
    email: '',
    expectedPaymentDate: '',
    // Business info
    companyName: '',
    position: '',
    registrationNumber: '',
    businessAddress: '',
    fleetSize: 1,
    // Credit info (for business credit payments)
    mainBank: '',
    yearEstablished: '',
    numberOfEmployees: '',
    monthlyRevenue: ''
  });
  const [paymentPlan, setPaymentPlan] = useState<PaymentPlan | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (formData.downPayment >= MIN_DOWNPAYMENT) {
      calculatePaymentPlan();
    }
  }, [formData.downPayment, formData.duration]);

  const calculatePaymentPlan = () => {
    const remainingAmount = productPrice - formData.downPayment;
    const monthlyPayment = remainingAmount / formData.duration;

    setPaymentPlan({
      downPayment: formData.downPayment,
      remainingAmount,
      monthlyPayment,
      duration: formData.duration,
      totalAmount: productPrice
    });
  };

  const handleDownloadPDF = () => {
    if (!paymentPlan) return;
    
    const doc = new jsPDF();
    
    // Add header
    doc.setFontSize(20);
    doc.text('Accord Pay Molo', 20, 20);
    
    // Add details
    doc.setFontSize(12);
    doc.text(`Modèle: ${productName}`, 20, 40);
    doc.text(`Prix total: ${formatCurrency(productPrice)}`, 20, 50);
    doc.text(`Acompte: ${formatCurrency(formData.downPayment)}`, 20, 60);
    doc.text(`Durée: ${formData.duration} mois`, 20, 70);
    doc.text(`Paiement mensuel: ${formatCurrency(Math.round(paymentPlan.monthlyPayment))}`, 20, 80);
    doc.text(`Reste à payer: ${formatCurrency(paymentPlan.remainingAmount)}`, 20, 90);
    
    // Add terms
    doc.setFontSize(10);
    doc.text('La moto sera livrée uniquement après le paiement intégral.', 20, 110);
    
    // Save the PDF
    doc.save('accord-pay-molo.pdf');
    toast.success('Accord téléchargé en PDF');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    setSubmitting(true);
    // The actual form submission is handled by EmailFormHandler
    // This is just for UI feedback
    setTimeout(() => {
      handleDownloadPDF();
      onClose();
      setSubmitting(false);
    }, 1500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-2xl font-bold text-neutral-900">Pay Molo</h3>
            <p className="text-gray-600">Payez votre moto en toute simplicité avant de venir la récupérer</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {/* Account Type Selection */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setAccountType('individual')}
              className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                accountType === 'individual'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-primary-200'
              }`}
            >
              <User size={24} className={`mx-auto mb-2 ${accountType === 'individual' ? 'text-primary-500' : 'text-gray-400'}`} />
              <div className={`font-medium ${accountType === 'individual' ? 'text-primary-500' : 'text-gray-600'}`}>
                Particulier
              </div>
            </button>
            <button
              onClick={() => setAccountType('business')}
              className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                accountType === 'business'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-primary-200'
              }`}
            >
              <Building2 size={24} className={`mx-auto mb-2 ${accountType === 'business' ? 'text-primary-500' : 'text-gray-400'}`} />
              <div className={`font-medium ${accountType === 'business' ? 'text-primary-500' : 'text-gray-600'}`}>
                Entreprise
              </div>
            </button>
          </div>

          {/* Payment Mode Selection */}
          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-3">Mode de paiement</label>
            <div className="flex gap-4">
              <button
                onClick={() => setPaymentMode('cash')}
                className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                  paymentMode === 'cash'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-green-200'
                }`}
              >
                <CreditCard size={24} className={`mx-auto mb-2 ${paymentMode === 'cash' ? 'text-green-500' : 'text-gray-400'}`} />
                <div className={`font-medium ${paymentMode === 'cash' ? 'text-green-600' : 'text-gray-600'}`}>
                  Paiement au comptant
                </div>
              </button>
              <button
                onClick={() => setPaymentMode('credit')}
                className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                  paymentMode === 'credit'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-200'
                }`}
              >
                <Calendar size={24} className={`mx-auto mb-2 ${paymentMode === 'credit' ? 'text-blue-500' : 'text-gray-400'}`} />
                <div className={`font-medium ${paymentMode === 'credit' ? 'text-blue-600' : 'text-gray-600'}`}>
                  Paiement par crédit
                </div>
              </button>
            </div>
          </div>

          <EmailFormHandler formName="Demande Pay Molo" className="space-y-6" onSubmit={handleSubmit}>
            {/* Hidden fields for email template */}
            <input type="hidden" name="product_name" value={productName} />
            <input type="hidden" name="product_price" value={productPrice.toString()} />
            <input type="hidden" name="account_type" value={accountType} />
            
            {/* Payment Details */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Détails du Paiement</h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Acompte Initial (minimum {formatCurrency(MIN_DOWNPAYMENT)}) *
                  </label>
                  <div className="space-y-4">
                    <input
                      type="range"
                      min={MIN_DOWNPAYMENT}
                      max={productPrice}
                      step={10000}
                      value={formData.downPayment}
                      onChange={(e) => setFormData({ ...formData, downPayment: parseInt(e.target.value) })}
                      className="w-full accent-primary-500"
                    />
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Minimum: {formatCurrency(MIN_DOWNPAYMENT)}</span>
                      <span className="text-lg font-medium text-primary-600">{formatCurrency(formData.downPayment)}</span>
                    </div>
                  </div>
                </div>
                <input type="hidden" name="down_payment" value={formData.downPayment.toString()} />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Durée de financement *
                  </label>
                  <div className="grid grid-cols-5 gap-3">
                    {PAYMENT_PERIODS.map(period => (
                      <button
                        key={period}
                        type="button"
                        onClick={() => setFormData({ ...formData, duration: period })}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          formData.duration === period
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-200 hover:border-primary-200 text-gray-600'
                        }`}
                      >
                        {period} mois
                      </button>
                    ))}
                  </div>
                </div>
                <input type="hidden" name="duration" value={formData.duration.toString()} />

                {paymentPlan && (
                  <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900">Récapitulatif</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Prix total</p>
                        <p className="text-lg font-medium">{formatCurrency(productPrice)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Acompte</p>
                        <p className="text-lg font-medium">{formatCurrency(formData.downPayment)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Reste à payer</p>
                        <p className="text-lg font-medium">{formatCurrency(paymentPlan.remainingAmount)}</p>
                        <input type="hidden" name="remaining_amount" value={paymentPlan.remainingAmount.toString()} />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Paiement mensuel</p>
                        <p className="text-lg font-medium">{formatCurrency(Math.round(paymentPlan.monthlyPayment))}</p>
                        <input type="hidden" name="monthly_payment" value={Math.round(paymentPlan.monthlyPayment).toString()} />
                      </div>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg mt-4 flex items-start">
                      <AlertTriangle size={20} className="text-yellow-600 mr-3 flex-shrink-0 mt-1" />
                      <p className="text-yellow-700 text-sm">
                        La moto sera livrée uniquement après le paiement intégral du montant restant.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Personal/Business Information */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                {accountType === 'individual' ? 'Informations Personnelles' : 'Informations de l\'Entreprise'}
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {accountType === 'business' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom de l'entreprise *
                      </label>
                      <div className="relative">
                        <Building2 size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          name="company_name"
                          value={formData.companyName}
                          onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Numéro d'immatriculation *
                      </label>
                      <div className="relative">
                        <FileText size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          name="registration_number"
                          value={formData.registrationNumber}
                          onChange={(e) => setFormData({ ...formData, registrationNumber: e.target.value })}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Poste du responsable *
                      </label>
                      <div className="relative">
                        <Briefcase size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          name="position"
                          value={formData.position}
                          onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Taille de la flotte *
                      </label>
                      <input
                        type="number"
                        min="1"
                        name="fleet_size"
                        value={formData.fleetSize}
                        onChange={(e) => setFormData({ ...formData, fleetSize: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>

                    {/* Additional fields for Business Credit Payment */}
                    {paymentMode === 'credit' && (
                      <>
                        <div className="md:col-span-2">
                          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-4">
                            <p className="text-sm text-blue-800 font-medium">
                              <AlertTriangle size={16} className="inline mr-2" />
                              Informations supplémentaires requises pour l'évaluation de crédit
                            </p>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Banque principale *
                          </label>
                          <input
                            type="text"
                            name="main_bank"
                            value={formData.mainBank}
                            onChange={(e) => setFormData({ ...formData, mainBank: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                            placeholder="Ex: BICIS, Ecobank, UBA..."
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Année de création *
                          </label>
                          <input
                            type="number"
                            min="1900"
                            max={new Date().getFullYear()}
                            name="year_established"
                            value={formData.yearEstablished}
                            onChange={(e) => setFormData({ ...formData, yearEstablished: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                            placeholder="Ex: 2018"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nombre d'employés *
                          </label>
                          <input
                            type="number"
                            min="1"
                            name="number_of_employees"
                            value={formData.numberOfEmployees}
                            onChange={(e) => setFormData({ ...formData, numberOfEmployees: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                            placeholder="Ex: 15"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Revenu mensuel estimé (FCFA) *
                          </label>
                          <input
                            type="number"
                            min="0"
                            name="monthly_revenue"
                            value={formData.monthlyRevenue}
                            onChange={(e) => setFormData({ ...formData, monthlyRevenue: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                            placeholder="Ex: 5000000"
                            required
                          />
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom complet *
                      </label>
                      <div className="relative">
                        <User size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          name="full_name"
                          value={formData.fullName}
                          onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Numéro de permis *
                      </label>
                      <div className="relative">
                        <FileText size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          name="license_number"
                          value={formData.licenseNumber}
                          onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          required
                        />
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Téléphone *
                  </label>
                  <div className="relative">
                    <Phone size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {accountType === 'business' ? 'Adresse de l\'entreprise' : 'Quartier'} *
                  </label>
                  <div className="relative">
                    <MapPin size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name={accountType === 'business' ? 'business_address' : 'neighborhood'}
                      value={accountType === 'business' ? formData.businessAddress : formData.neighborhood}
                      onChange={(e) => setFormData(accountType === 'business' 
                        ? { ...formData, businessAddress: e.target.value }
                        : { ...formData, neighborhood: e.target.value }
                      )}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de paiement prévue *
                  </label>
                  <div className="relative">
                    <Calendar size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="date"
                      name="expected_payment_date"
                      value={formData.expectedPaymentDate}
                      onChange={(e) => setFormData({ ...formData, expectedPaymentDate: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={!paymentPlan || submitting}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 inline-flex items-center"
              >
                {submitting ? 'Envoi en cours...' : (
                  <>
                    Envoyer la demande Pay Molo
                    <ArrowRight size={20} className="ml-2" />
                  </>
                )}
              </button>
            </div>
          </EmailFormHandler>
        </div>
      </div>
    </div>
  );
};

export default PayMoloForm;